import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  User, 
  Mail, 
  MapPin, 
  Phone, 
  Instagram, 
  MessageSquare,
  Calendar,
  Image,
  ArrowLeft,
  CheckCircle,
  Clock,
  FileText,
  ExternalLink,
  Lock,
  LogIn,
  UserCheck,
  AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;
const API = `${BACKEND_URL}/api`;

const ViewApplicationPage = () => {
  const { viewKey } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [contactHidden, setContactHidden] = useState(false);
  const [handling, setHandling] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (err) {
        console.error("Failed to parse stored user:", err);
      }
    }
    
    if (viewKey) {
      fetchApplication();
    }
  }, [viewKey]);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/view/${viewKey}`);
      setApplication(response.data);
      setContactHidden(response.data.contact_hidden === true);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        setRequiresAuth(true);
        setError("Authentication required to view this application.");
      } else if (err.response?.status === 403) {
        setError("You don't have permission to view this application.");
      } else if (err.response?.status === 404) {
        setError("Application not found. The link may be invalid or expired.");
      } else {
        setError(err.response?.data?.detail || "Failed to load application. Please try again later.");
      }
      setLoading(false);
    }
  };

  const handleApplication = async () => {
    if (!application?.id) return;
    
    setHandling(true);
    try {
      
      await axios.post(
        `${API}/admin/applications/${application.id}/handle`,
        {},
        { headers: {} }
      );
      // Refresh to get full application data
      await fetchApplication();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to handle application");
    } finally {
      setHandling(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00A8E8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading application...</p>
        </div>
      </div>
    );
  }

  // Authentication Required Screen
  if (requiresAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock size={40} className="text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Authentication Required</h1>
          <p className="text-gray-600 mb-8">
            You need to be logged in to view application details. 
            Please sign in with your admin or manager account.
          </p>
          <Link 
            to="/admin/login"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            <LogIn size={18} />
            Sign In
          </Link>
          <p className="text-gray-500 text-sm mt-4">
            <Link to="/" className="text-[#00A8E8] hover:underline">← Back to Homepage</Link>
          </p>
        </div>
      </div>
    );
  }

  if (error && !requiresAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText size={40} className="text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Oops!</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            <ArrowLeft size={18} />
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Contact Hidden - Show limited info with Handle button
  if (contactHidden) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4" data-testid="view-application-page">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
              data-testid="back-link"
            >
              <ArrowLeft size={18} />
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] rounded-xl flex items-center justify-center">
                <User size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Model Application
                </h1>
                <p className="text-gray-500 text-sm">Application from {application.name}</p>
              </div>
            </div>
          </motion.div>

          {/* Limited Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
            data-testid="application-card"
          >
            {/* Status Banner */}
            <div className="bg-gradient-to-r from-[#00A8E8] to-[#D000FF] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <Calendar size={18} />
                <span className="text-sm opacity-90">Submitted: {formatDate(application.created_at)}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.status)}`}>
                {application.status?.charAt(0).toUpperCase() + application.status?.slice(1) || 'Pending'}
              </span>
            </div>

            {/* Limited Details */}
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Name */}
                <div className="bg-gray-50 rounded-xl p-4" data-testid="field-name">
                  <div className="flex items-center gap-3 mb-1">
                    <User size={18} className="text-[#00A8E8]" />
                    <span className="text-sm text-gray-500">Full Name</span>
                  </div>
                  <p className="font-semibold text-gray-800 pl-8">{application.name}</p>
                </div>

                {/* Country */}
                <div className="bg-gray-50 rounded-xl p-4" data-testid="field-country">
                  <div className="flex items-center gap-3 mb-1">
                    <MapPin size={18} className="text-[#D000FF]" />
                    <span className="text-sm text-gray-500">Country</span>
                  </div>
                  <p className="font-semibold text-gray-800 pl-8">{application.country}</p>
                </div>
              </div>

              {/* Contact Hidden Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                <Lock size={40} className="text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Contact Information Hidden</h3>
                <p className="text-gray-600 mb-6">
                  {application.assigned_manager_name 
                    ? `This application is assigned to ${application.assigned_manager_name}. You cannot view the contact details.`
                    : "You must handle this application to reveal contact details (email, phone, social media, message)."}
                </p>
                
                {!application.assigned_manager_id && (
                  <button
                    onClick={handleApplication}
                    disabled={handling}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    data-testid="handle-btn"
                  >
                    {handling ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <UserCheck size={20} />
                    )}
                    Handle This Application
                  </button>
                )}
                
                {application.assigned_manager_name && (
                  <div className="inline-flex items-center gap-2 text-gray-600 bg-gray-100 px-4 py-2 rounded-full">
                    <AlertCircle size={18} />
                    Assigned to: {application.assigned_manager_name}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Application ID: {application.id}</span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {formatDate(application.created_at)}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Full Application View (for admins and assigned managers)
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4" data-testid="view-application-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/admin"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
            data-testid="back-link"
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] rounded-xl flex items-center justify-center">
              <User size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Model Application
              </h1>
              <p className="text-gray-500 text-sm">Application details for {application.name}</p>
            </div>
          </div>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
          data-testid="application-card"
        >
          {/* Status Banner */}
          <div className="bg-gradient-to-r from-[#00A8E8] to-[#D000FF] px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 text-white">
              <Calendar size={18} />
              <span className="text-sm opacity-90">Submitted: {formatDate(application.created_at)}</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.status)}`}>
              {application.status?.charAt(0).toUpperCase() + application.status?.slice(1) || 'Pending'}
            </span>
          </div>

          {/* Photo Section */}
          {application.photo_data && (
            <div className="p-6 border-b border-gray-100" data-testid="photo-section">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Image size={16} />
                Uploaded Photo
              </h3>
              <div className="relative">
                <img
                  src={`data:image/jpeg;base64,${application.photo_data}`}
                  alt={`${application.name}'s photo`}
                  className="w-full max-w-md mx-auto rounded-xl shadow-lg object-cover"
                  data-testid="application-photo"
                />
                {application.photo_filename && (
                  <p className="text-center text-gray-400 text-sm mt-2">{application.photo_filename}</p>
                )}
              </div>
            </div>
          )}

          {/* Details Grid */}
          <div className="p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Applicant Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Name */}
              <div className="bg-gray-50 rounded-xl p-4" data-testid="field-name">
                <div className="flex items-center gap-3 mb-1">
                  <User size={18} className="text-[#00A8E8]" />
                  <span className="text-sm text-gray-500">Full Name</span>
                </div>
                <p className="font-semibold text-gray-800 pl-8">{application.name}</p>
              </div>

              {/* Email */}
              <div className="bg-gray-50 rounded-xl p-4" data-testid="field-email">
                <div className="flex items-center gap-3 mb-1">
                  <Mail size={18} className="text-[#00A8E8]" />
                  <span className="text-sm text-gray-500">Email Address</span>
                </div>
                <a 
                  href={`mailto:${application.email}`}
                  className="font-semibold text-[#00A8E8] hover:underline pl-8 block"
                >
                  {application.email}
                </a>
              </div>

              {/* Country */}
              <div className="bg-gray-50 rounded-xl p-4" data-testid="field-country">
                <div className="flex items-center gap-3 mb-1">
                  <MapPin size={18} className="text-[#D000FF]" />
                  <span className="text-sm text-gray-500">Country</span>
                </div>
                <p className="font-semibold text-gray-800 pl-8">{application.country}</p>
              </div>

              {/* WhatsApp */}
              <div className="bg-gray-50 rounded-xl p-4" data-testid="field-whatsapp">
                <div className="flex items-center gap-3 mb-1">
                  <Phone size={18} className="text-[#25D366]" />
                  <span className="text-sm text-gray-500">WhatsApp</span>
                </div>
                <a 
                  href={`https://wa.me/${application.whatsapp?.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#25D366] hover:underline pl-8 flex items-center gap-1"
                >
                  {application.whatsapp}
                  <ExternalLink size={14} />
                </a>
              </div>

              {/* Instagram */}
              {application.instagram && (
                <div className="bg-gray-50 rounded-xl p-4" data-testid="field-instagram">
                  <div className="flex items-center gap-3 mb-1">
                    <Instagram size={18} className="text-[#E4405F]" />
                    <span className="text-sm text-gray-500">Instagram</span>
                  </div>
                  <a 
                    href={application.instagram.startsWith('http') ? application.instagram : `https://instagram.com/${application.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#E4405F] hover:underline pl-8 flex items-center gap-1"
                  >
                    {application.instagram}
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}

              {/* TikTok */}
              {application.tiktok && (
                <div className="bg-gray-50 rounded-xl p-4" data-testid="field-tiktok">
                  <div className="flex items-center gap-3 mb-1">
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    <span className="text-sm text-gray-500">TikTok</span>
                  </div>
                  <a 
                    href={application.tiktok.startsWith('http') ? application.tiktok : `https://tiktok.com/@${application.tiktok.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-gray-800 hover:underline pl-8 flex items-center gap-1"
                  >
                    {application.tiktok}
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}

              {/* Twitter/X */}
              {application.twitter && (
                <div className="bg-gray-50 rounded-xl p-4" data-testid="field-twitter">
                  <div className="flex items-center gap-3 mb-1">
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span className="text-sm text-gray-500">Twitter/X</span>
                  </div>
                  <a 
                    href={application.twitter.startsWith('http') ? application.twitter : `https://x.com/${application.twitter.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-gray-800 hover:underline pl-8 flex items-center gap-1"
                  >
                    {application.twitter}
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}

              {/* OnlyFans */}
              {application.onlyfans && (
                <div className="bg-gray-50 rounded-xl p-4" data-testid="field-onlyfans">
                  <div className="flex items-center gap-3 mb-1">
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="#00AFF0">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.5a7.5 7.5 0 110 15 7.5 7.5 0 010-15zm0 3a4.5 4.5 0 100 9 4.5 4.5 0 000-9z"/>
                    </svg>
                    <span className="text-sm text-gray-500">OnlyFans</span>
                  </div>
                  <a 
                    href={application.onlyfans.startsWith('http') ? application.onlyfans : `https://onlyfans.com/${application.onlyfans.replace('onlyfans.com/', '').replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#00AFF0] hover:underline pl-8 flex items-center gap-1"
                  >
                    {application.onlyfans}
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>

            {/* Message */}
            {application.message && (
              <div className="mt-6" data-testid="field-message">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MessageSquare size={16} />
                  Message
                </h3>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{application.message}</p>
                </div>
              </div>
            )}

            {/* Terms Agreement */}
            <div className="mt-6 flex items-center gap-2 text-gray-500" data-testid="terms-agreed">
              <CheckCircle size={18} className="text-green-500" />
              <span className="text-sm">Agreed to Terms & Conditions</span>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Application ID: {application.id}</span>
              <span className="flex items-center gap-1">
                <Clock size={14} />
                {formatDate(application.created_at)}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Footer Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <p className="text-gray-500 text-sm">
            Powered by{" "}
            <Link to="/" className="text-[#00A8E8] hover:underline font-medium">
              HotPeek Agency
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ViewApplicationPage;
